<template>
  <v-dialog
    :value="isOpen"
    scrollable
    persistent
    :max-width="maxWidth"
    content-class="gmb-attributes-tree"
  >
    <v-card>
      <v-card-title class="pb-0">
        <div class="w-100 text-center">
          {{ $t(title) }}
        </div>
        <div v-if="message" class="dialog-message">
          {{ $t(message) }}
        </div>
        <v-tabs
          v-model="tab"
          class="gmb-attributes-tree__tabs "
          :class="{ 'd-none': allowedTabItems.length === 1 }"
        >
          <v-tabs-slider color="primary"></v-tabs-slider>

          <v-tab
            v-for="item in allowedTabItems"
            :key="item.id"
            style="text-transform: none; letter-spacing: normal;"
          >
            {{ item.name }}
          </v-tab>
        </v-tabs>
      </v-card-title>
      <v-card-text class="gmb-attributes-tree__content">
        <v-tabs-items v-model="tab">
          <v-tab-item>
            <v-text-field
              :value="search"
              prepend-inner-icon="icon-search"
              :label="$t('message.facilityDetails.services.findService')"
              single-line
              clearable
              class="search-input"
              :hide-details="true"
              @input="onInput"
            ></v-text-field>
            <template v-if="filteredServices.length && !loading">
              <GmbServicesTreeNode
                v-for="groupName in filteredServices"
                :key="groupName.category.id"
                :group-name="groupName.category.name"
                :services="groupName.service_types"
                :delete-mode="deleteMode"
                @input="handleSelectedServices($event, groupName.category.id)"
                @update="handleSelectedServiceUpdate"
              />
            </template>
            <no-data v-else></no-data>
            <SectionLoader :status="searching" />
          </v-tab-item>
          <v-tab-item>
            <GmbServiceMassCustomService
              ref="customService"
              :delete-mode="deleteMode"
              :facilities-ids="facilitiesIds"
              :preselected-custom-services="preselectedCustomServices"
              @validate="handleValidation"
              @change="handleCustomServiceChange"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
      <v-card-actions class="justify-center">
        <ui-button class="mr-2" color="primary" outlined @click="close">
          {{ $t("message.common.cancel") }}
        </ui-button>
        <ui-button
          class="ml-2"
          color="primary"
          :disabled="!isFormValid"
          @click="save"
        >
          {{ $t("message.common.apply") }}
        </ui-button>
      </v-card-actions>
      <SectionLoader :status="loading" :opacity="1" />
    </v-card>
  </v-dialog>
</template>

<script>
import GmbServicesTreeNode from "@/components/FacilityDetails/GmbServices/GmbServicesTreeNode";
import GmbServiceMassCustomService from "@/components/FacilityDetails/GmbServices/GmbServiceMassCustomService.vue";
import SectionLoader from "@/components/Loader/SectionLoader";
import NoData from "@/components/ui/NoData";
import { SERVICE_ITEM_TYPE_ENUM } from "@/enums/gmb-services.enum";
import { debounce } from "@/helpers/helpers";
import gmbServices from "@/services/gmbServices";
import serviceHandler from "@/helpers/handlers/service";
import cuid from "cuid";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";

export default {
  name: "GmbServicesTree",
  components: {
    GmbServicesTreeNode,
    SectionLoader,
    NoData,
    GmbServiceMassCustomService
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    preselectedServicesList: {
      type: Array,
      default: () => []
    },
    preselectedCustomServices: {
      type: Array,
      default: () => []
    },
    facilitiesIds: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      default: () => "message.facilityDetails.attributes.title"
    },
    message: {
      type: String,
      default: () => ""
    },
    deleteMode: {
      type: Boolean,
      default: () => false
    }
  },
  data() {
    return {
      loading: false,
      maxWidth: 533,
      search: "",
      searching: false,
      count: 0,
      services: [],
      servicesPayload: { service_items: [] },
      selectedServicesWithCategories: [],
      tab: null,
      isFormValid: true,
      tabItems: [
        {
          id: cuid(),
          name: this.$t("message.facilityDetails.services.standard")
        },
        {
          id: cuid(),
          name: this.$t("message.facilityDetails.services.custom")
        }
      ]
    };
  },
  computed: {
    preselectedServicesListLocal() {
      return this.preselectedServicesList.map(
        ({ gmb_service_type, description }) => {
          return {
            ...gmb_service_type,
            description: description || gmb_service_type.description || ""
          };
        }
      );
    },
    preselectedServicesIds() {
      return this.preselectedServicesListLocal.map(
        service => service.service_type_id
      );
    },
    filteredServices() {
      const searchLower = this.search.toLowerCase();
      return this.services
        .map(group => {
          return {
            ...group,
            service_types: group.service_types.filter(item =>
              item.display_name.toLowerCase().includes(searchLower)
            )
          };
        })
        .filter(group => group.service_types.length > 0);
    },
    allowedTabItems() {
      if (isFeatureFlagEnabled(FEATURE_FLAG.CN_6392_MASS_SERVICES_MANAGING)) {
        return this.tabItems;
      }
      return [this.tabItems[0]];
    }
  },
  async created() {
    await this.fetchServices();
  },
  methods: {
    handleValidation(isFormValid) {
      this.isFormValid = isFormValid;
    },
    handleSelectedServiceUpdate(service) {
      const serviceItem = this.servicesPayload.service_items.find(
        item =>
          item.gmb_service_type &&
          item.gmb_service_type.service_type_id === service.service_type_id
      );

      if (serviceItem) {
        serviceItem.description = service.description;
      }

      this.services = this.services.map(category => {
        const updatedCategory = { ...category };
        updatedCategory.service_types = category.service_types.map(type => {
          if (type.service_type_id === service.service_type_id) {
            return { ...type, description: service.description };
          }
          return type;
        });
        return updatedCategory;
      });
    },
    handleCustomServiceChange(customServicesList) {
      const existingItems = this.servicesPayload.service_items;
      const updatedWithDescription = customServicesList.service_items.map(
        item => {
          const existingItem = existingItems.find(
            existingItem =>
              existingItem.display_name === item.display_name &&
              existingItem.type === SERVICE_ITEM_TYPE_ENUM.FREE_FORM
          );
          if (existingItem) {
            return {
              ...existingItem,
              description: item.description
            };
          }
          return item;
        }
      );
      const newItems = customServicesList.service_items.filter(
        newItem =>
          !updatedWithDescription.some(
            updatedWithDescription =>
              updatedWithDescription.display_name === newItem.display_name
          )
      );
      this.servicesPayload.service_items = [
        ...this.servicesPayload.service_items
          .filter(item => item.gmb_service_type)
          .map(item => {
            return {
              ...item,
              description:
                item.description || item.gmb_service_type.description || ""
            };
          }),
        ...updatedWithDescription,
        ...newItems
      ];
    },
    setPayload(service) {
      const { service_items } = this.servicesPayload;
      const index = service_items.findIndex(
        item =>
          item.gmb_service_type &&
          item.gmb_service_type.service_type_id === service.service_type_id
      );

      if (index !== -1) {
        service_items.splice(index, 1);
      } else {
        const localService = { ...service };
        delete localService.value;
        service_items.push({
          description: service.description,
          gmb_service_type: localService,
          type: SERVICE_ITEM_TYPE_ENUM.STRUCTURED
        });
      }
    },
    handleSelectedServices(service, categoryId) {
      this.setPayload(service);
      this.updateServiceTypes(service, categoryId);
      this.filterSelectedServicesWithCategories();
    },

    updateServiceTypes(service, categoryId) {
      this.services.forEach(group => {
        if (group.category.id === categoryId) {
          group.service_types = group.service_types.map(item => {
            if (item.service_type_id === service.service_type_id) {
              return service;
            }
            return item;
          });
        }
      });
    },

    filterSelectedServicesWithCategories() {
      const servicePayloadWithCategory = this.servicesPayload.service_items.filter(
        item => item.gmb_service_type
      );
      const selectedServiceTypeIds = new Set(
        servicePayloadWithCategory.map(
          el => el.gmb_service_type.service_type_id
        )
      );

      this.selectedServicesWithCategories = this.services
        .map(service => ({
          ...service,
          service_types: service.service_types.filter(item =>
            selectedServiceTypeIds.has(item.service_type_id)
          )
        }))
        .filter(service => service.service_types.length > 0);
    },
    async fetchServices() {
      this.loading = true;

      await this.loadServices();
      this.filterServices();
      this.preselectServices();
      this.addPreselectedServices();

      this.loading = false;
    },

    async loadServices() {
      this.services = await serviceHandler(
        gmbServices.getGmbServicesWithCategories({
          facilities_ids: this.facilitiesIds.join(","),
          only_common: true,
          only_set: this.deleteMode
        })
      );
    },

    filterServices() {
      this.services = this.services.filter(
        service => service.service_types.length
      );
    },

    preselectServices() {
      this.services.forEach(group => {
        group.service_types = group.service_types.map(item => {
          const description = (
            this.preselectedServicesListLocal.find(
              service => service.service_type_id === item.service_type_id
            ) || {}
          ).description;

          return {
            ...item,
            ...(description && { description }),
            value: this.preselectedServicesIds.includes(item.service_type_id)
              ? true
              : item.value
          };
        });
      });
    },
    setPreselectedCustomServices() {
      return this.servicesPayload.service_items.push(
        ...this.preselectedCustomServices
      );
    },
    addPreselectedServices() {
      const preselectedServices = this.getPreselectedServices();
      this.updateServicePayloadItems(preselectedServices);
    },

    getPreselectedServices() {
      return this.services
        .flatMap(group => group.service_types)
        .filter(item =>
          this.preselectedServicesIds.includes(item.service_type_id)
        )
        .map(item => ({
          gmb_service_type: item,
          description: item.description ? item.description : ""
        }));
    },

    updateServicePayloadItems(preselectedServices) {
      this.servicesPayload.service_items = [
        ...this.servicesPayload.service_items,
        ...this.preselectedCustomServices,
        ...preselectedServices
      ];
    },
    close() {
      this.$emit("close");
    },
    save() {
      this.filterSelectedServicesWithCategories();
      this.servicesPayload.service_items = this.servicesPayload.service_items.map(
        item => {
          if (item.gmb_service_type) {
            return {
              ...item,
              type: SERVICE_ITEM_TYPE_ENUM.STRUCTURED
            };
          }
          return item;
        }
      );

      this.$emit("save", {
        payload: {
          ...this.servicesPayload,
          type: 1
        },
        selectedServicesWithCategories: this.selectedServicesWithCategories
      });

      this.close();
    },
    onInput(value) {
      this.searching = true;
      this.setSearch(value);
    },
    setSearch: debounce(function(value) {
      this.search = value;
      this.searching = false;
    }, 500)
  }
};
</script>

<style lang="scss">
$paddingX: 68px;
$shadow: 0 0 12px rgba(0, 0, 0, 0.05);

.gmb-attributes-tree {
  &__tabs {
    border-bottom: 1px solid #dedede;
    width: 100%;
    margin-left: -65px;
    margin-right: -65px;
    display: flex;
    justify-content: center;
    margin: auto;
  }
  .v-card {
    .v-card__title {
      @include font-size(18px);
      font-weight: 600;
      line-height: 22px;
      justify-content: center;
    }

    .v-card__title,
    .v-card__actions {
      padding: 20px $paddingX;
      box-shadow: $shadow;
      z-index: 1;

      .v-btn {
        flex-grow: 1;
      }
    }

    .v-card__text {
      padding-left: $paddingX;
      padding-right: $paddingX;
      position: relative;
    }
  }

  .search-input {
    width: 100%;

    .theme--light.v-label {
      color: rgba(54, 60, 86, 0.35);
      font-weight: 400;
    }

    &.v-text-field > .v-input__control > .v-input__slot {
      font-weight: 500;
    }
  }

  .dialog-message {
    @include font-size(14px);
    line-height: 17px;
    text-align: center;
    color: $grey-darker;
    opacity: 0.8;
    font-weight: 400;
    word-break: normal;
    margin: $spacing * 1.25 $spacing * -5;
  }
}
</style>
